/**
 * Child component role annotations — describes the purpose of auto-created
 * children so AI agents understand container component structure.
 *
 * Pattern mirrors component-purposes.ts: static Record keyed by component name.
 */

export interface IChildRoleAnnotation {
  role: string;
  isContainer: boolean;
  note?: string;
}

/** Role annotations keyed by child component type name. */
export const CHILD_ROLE_ANNOTATIONS: Record<string, IChildRoleAnnotation> = {
  // Table children
  TableHeader: {
    role: "Column header labels row",
    isContainer: false,
    note: "Add column components here for header labels. NEVER delete this."
  },
  TableRow: {
    role: "Data template repeater — cloned per data row at runtime",
    isContainer: false,
    note: "Add column components here. Looks like 1 row in designer but renders many at runtime. NEVER delete this."
  },
  TableDefaultColumn: {
    role: "Auto-bound data column with built-in Label — not a droppable container",
    isContainer: false
  },
  TableTemplateColumn: {
    role: "Custom content column — droppable container for buttons, icons, etc.",
    isContainer: true
  },
  TableColumn: {
    role: "Legacy custom content column — droppable container",
    isContainer: true
  },
  EditableTableColumn: {
    role: "Inline-editable data column — not a droppable container",
    isContainer: false
  },

  // ReportTable children
  ReportTableHeader: {
    role: "Report table header row — fixed structural child",
    isContainer: false,
    note: "Cannot be deleted independently."
  },
  ReportTableRow: {
    role: "Report table data row — fixed structural child",
    isContainer: false,
    note: "Cannot be deleted independently."
  },
  ReportTableColumn: {
    role: "Report table column — droppable container for custom content",
    isContainer: true
  },

  // StaticTable children
  StaticTableHeader: {
    role: "Static table header row",
    isContainer: false,
    note: "NEVER delete this."
  },
  StaticTableRow: {
    role: "Static table data row",
    isContainer: false,
    note: "NEVER delete this."
  },
  StaticTableColumn: {
    role: "Static table column — droppable container for custom content",
    isContainer: true
  },

  // Tab children
  TabItem: {
    role: "Tab panel — droppable container for tab content",
    isContainer: true
  },

  // Collapse children
  CollapsePanel: {
    role: "Accordion panel — droppable container for collapsible content",
    isContainer: true
  },

  // Splitter children (KOA-13797)
  SplitterPanel: {
    role: "Resizable split pane — droppable container for one side of a Splitter",
    isContainer: true
  },

  // Menu children
  SubMenu: {
    role: "Submenu group — contains MenuItem or nested SubMenu children",
    isContainer: true
  },
  MenuItem: {
    role: "Menu item — clickable navigation entry",
    isContainer: false
  }

  // Carousel children (uses Column)
  // Column is too generic to annotate here — handled contextually
};

/**
 * Structural children that cannot be deleted independently from their parent table.
 * Deleting these would break the table's ability to render.
 */
export const PROTECTED_TABLE_CHILDREN: ReadonlySet<string> = new Set([
  "TableRow",
  "TableHeader",
  "ReportTableRow",
  "ReportTableHeader",
  "StaticTableRow",
  "StaticTableHeader"
]);
